import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContextType } from '../types';
import { api } from '../lib/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthContextType['user']>(null);
  const [permissions, setPermissions] = useState<AuthContextType['permissions']>({
    canManageStaff: false,
    canDeleteLinks: false,
    canEditLinks: false,
    canViewAnalytics: false,
    canManageCampaigns: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [logoutUrl, setLogoutUrl] = useState('/');
  const [devUserEmail, setDevUserEmailState] = useState(
    localStorage.getItem('bro_dev_user_email') || 'marketing@brototype.com'
  );

  const fetchProfile = useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('bro_auth_token') : null;
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // If not on localhost and no session token exists, user is strictly unauthenticated
    if (!token && !isLocalhost) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await api.get<{
        user: any;
        permissions: any;
        logoutUrl?: string;
      }>('/api/auth/me');

      setUser(data.user);
      setPermissions(data.permissions);
      setLogoutUrl(data.logoutUrl || '/login');
    } catch (err) {
      localStorage.removeItem('bro_auth_token');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      const res = await api.post<{
        success: boolean;
        token: string;
        user: any;
      }>('/api/auth/login', { email, password });

      if (res && res.token) {
        localStorage.setItem('bro_auth_token', res.token);
        localStorage.setItem('bro_dev_user_email', res.user.email);
        setUser(res.user);
        setPermissions({
          canManageStaff: res.user.role === 'admin',
          canDeleteLinks: res.user.role === 'admin',
          canEditLinks: true,
          canViewAnalytics: true,
          canManageCampaigns: true,
        });
        return { success: true };
      }
      return { success: false, error: 'Authentication failed' };
    } catch (err: any) {
      return { success: false, error: err.message || 'Invalid email or password' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await api.post('/api/auth/logout').catch(() => null);
    } finally {
      localStorage.removeItem('bro_auth_token');
      localStorage.removeItem('bro_dev_user_email');
      setUser(null);
      setIsLoading(false);
      window.location.href = '/login';
    }
  };

  const completeInviteLogin = (token: string, sessionUser: any) => {
    localStorage.setItem('bro_auth_token', token);
    localStorage.setItem('bro_dev_user_email', sessionUser.email);
    setUser(sessionUser);
    setPermissions({
      canManageStaff: sessionUser.role === 'admin',
      canDeleteLinks: sessionUser.role === 'admin',
      canEditLinks: true,
      canViewAnalytics: true,
      canManageCampaigns: true,
    });
  };

  const setDevUserEmail = (email: string) => {
    localStorage.setItem('bro_dev_user_email', email);
    setDevUserEmailState(email);
    fetchProfile();
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        isLoading,
        isAuthenticated: !!user,
        logoutUrl,
        devUserEmail,
        setDevUserEmail,
        login,
        logout,
        refreshUser: fetchProfile,
        completeInviteLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
